import { useState } from "react";
import { Dropdown, Input as AntInput, Select as AntSelect } from "antd";
import { get } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Switch from "shared/components/Switch";
import { NAME_SPACES } from "shared/locales/constants";
import { NODE_TYPES, PROCESS_TAGS } from "utils/constants";
import CanvasIcon from "./CanvasIcon";
import MirrorStatusListField from "./MirrorStatusListField";
import { NODE_TYPE_VALUES } from "./node-types";
import { ICON_PATHS, stepLook } from "./step-look";

const SettingsTab = ({
  node,
  processId,
  variables,
  isStart,
  automations,
  onChange,
  onStartHere,
  onDelete,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });
  const STATUSES = FORM.STATUSES || {};
  const TYPE_LABELS = FORM.NODE_TYPES || {};
  const TAGS = t("PROCESS_TAGS", { returnObjects: true });
  const SETTINGS = t("CANVAS_EDITOR.STEP.SETTINGS", { returnObjects: true });
  const TYPE_HINTS = t("CANVAS_EDITOR.TYPE_HINTS", { returnObjects: true });
  const DELETE_STEP = t("CANVAS_EDITOR.INSPECTOR.DELETE_STEP");
  const [legacyOpen, setLegacyOpen] = useState(false);

  const data = node.data || {};
  const nodeType = data.nodeType || NODE_TYPES.STATUS;
  const look = stepLook(nodeType);
  const tagOptions = Object.entries(PROCESS_TAGS).map(([key, value]) => ({
    value,
    label: TAGS[key] || key,
  }));
  const typeItems = NODE_TYPE_VALUES.map((value) => ({
    key: value,
    label: TYPE_LABELS[value] || value,
  }));

  const handleOpenSubscriptionChange = (checked) => {
    const config = { ...(data.config || {}) };
    if (checked) config.openSubscriptionOnEnter = true;
    else delete config.openSubscriptionOnEnter;
    onChange("config", config);
  };

  return (
    <div className="canvas-tab">
      <section className="canvas-tab__section canvas-tab__section--loose">
        <label className="canvas-form-field">
          <span className="canvas-form-field__label">{SETTINGS.NAME}</span>
          <AntInput
            className="canvas-form-field__control"
            value={data.name || ""}
            onChange={(event) => onChange("name", event.target.value)}
          />
        </label>
        <div className="canvas-form-field">
          <span className="canvas-form-field__label">{SETTINGS.TYPE}</span>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: typeItems,
              selectable: true,
              selectedKeys: [nodeType],
              onClick: ({ key }) => onChange("nodeType", key),
            }}
          >
            <button type="button" className="canvas-type-button">
              <span
                className={`canvas-type-button__icon canvas-tone--${look.tone} canvas-tone-fill--${look.tone}`}
              >
                <CanvasIcon path={look.icon} size={16} strokeWidth={2} />
              </span>
              <span className="canvas-type-button__text">
                <strong>{TYPE_LABELS[nodeType] || nodeType}</strong>
                <span>{get(TYPE_HINTS, nodeType)}</span>
              </span>
              <CanvasIcon
                path={ICON_PATHS.chevronDown}
                size={16}
                strokeWidth={2}
                className="canvas-tab__chevron"
              />
            </button>
          </Dropdown>
        </div>
        {onStartHere ? (
          <div className="canvas-start">
            {isStart ? (
              <span className="canvas-start__current">
                <CanvasIcon path={ICON_PATHS.flag} size={16} strokeWidth={2} />
                {SETTINGS.START_CURRENT}
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="canvas-start__button"
                  onClick={onStartHere}
                >
                  <CanvasIcon
                    path={ICON_PATHS.flag}
                    size={16}
                    strokeWidth={2}
                  />
                  {SETTINGS.START_HERE}
                </button>
                <span className="canvas-tab__note">{SETTINGS.START_HINT}</span>
              </>
            )}
          </div>
        ) : null}
        <div className="canvas-form-grid">
          <label className="canvas-form-field">
            <span className="canvas-form-field__label">{SETTINGS.TAG}</span>
            <AntSelect
              showSearch
              virtual={false}
              className="canvas-form-field__control"
              optionFilterProp="label"
              options={tagOptions}
              value={data.tag || undefined}
              onChange={(value) => onChange("tag", value)}
            />
          </label>
          <label className="canvas-form-field">
            <span className="canvas-form-field__label">
              {SETTINGS.PROGRESS}
            </span>
            <AntInput
              className="canvas-form-field__control"
              placeholder={SETTINGS.PROGRESS_PLACEHOLDER}
              value={data.progress || ""}
              onChange={(event) => onChange("progress", event.target.value)}
            />
          </label>
        </div>
        <span className="canvas-tab__note canvas-tab__note--tucked">
          {SETTINGS.LIST_HINT}
        </span>
        <label className="canvas-form-field">
          <span className="canvas-form-field__label">{SETTINGS.COLOR}</span>
          <AntInput
            className="canvas-form-field__control"
            placeholder={SETTINGS.COLOR_PLACEHOLDER}
            value={data.color || ""}
            onChange={(event) => onChange("color", event.target.value)}
          />
        </label>
      </section>

      {automations}

      <section className="canvas-toggle canvas-toggle--bordered">
        <div className="canvas-toggle__text">
          <strong>{SETTINGS.OPEN_SUBSCRIPTION}</strong>
          <span>{SETTINGS.OPEN_SUBSCRIPTION_HINT}</span>
        </div>
        <Switch
          aria-label={SETTINGS.OPEN_SUBSCRIPTION}
          checked={!!get(data, "config.openSubscriptionOnEnter")}
          onChange={handleOpenSubscriptionChange}
        />
      </section>

      <section className="canvas-tab__section canvas-tab__section--tight">
        <div>
          <h4 className="canvas-tab__subtitle">{SETTINGS.MIRROR_TITLE}</h4>
          <p className="canvas-tab__hint">{SETTINGS.MIRROR_HINT}</p>
        </div>
        <MirrorStatusListField
          actions={data.actions || []}
          processId={processId}
          variables={variables}
          conditionLabels={FORM.POSSIBLE_STATUSES?.CONDITIONS || {}}
          labels={{ ...STATUSES, DELETE: SETTINGS.MIRROR_DELETE }}
          onChange={(next) => onChange("actions", next)}
        />
      </section>

      <section className="canvas-tab__section">
        <button
          type="button"
          className="canvas-legacy"
          aria-expanded={legacyOpen}
          onClick={() => setLegacyOpen((open) => !open)}
        >
          <span className="canvas-legacy__text">
            <strong>{SETTINGS.LEGACY}</strong>
            <span>{SETTINGS.LEGACY_HINT}</span>
          </span>
          <CanvasIcon
            path={legacyOpen ? ICON_PATHS.chevronDown : ICON_PATHS.chevronRight}
            size={16}
            strokeWidth={2}
            className="canvas-tab__chevron"
          />
        </button>
        {legacyOpen ? (
          <div className="canvas-toggle canvas-toggle--bordered">
            <div className="canvas-toggle__text">
              <strong>{SETTINGS.MOVE_TO_NEXT}</strong>
            </div>
            <Switch
              aria-label={SETTINGS.MOVE_TO_NEXT}
              checked={!!data.moveToNext}
              onChange={(checked) => onChange("moveToNext", checked)}
            />
          </div>
        ) : null}
      </section>

      {onDelete ? (
        <button
          type="button"
          className="canvas-danger-button"
          onClick={onDelete}
        >
          <CanvasIcon path={ICON_PATHS.trash} size={16} strokeWidth={2} />
          {DELETE_STEP}
        </button>
      ) : null}
    </div>
  );
};

SettingsTab.propTypes = {
  node: PropTypes.object.isRequired,
  processId: PropTypes.string,
  variables: PropTypes.array,
  isStart: PropTypes.bool,
  automations: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onStartHere: PropTypes.func,
  onDelete: PropTypes.func,
};

SettingsTab.defaultProps = {
  processId: null,
  variables: [],
  isStart: false,
  automations: null,
  onStartHere: null,
  onDelete: null,
};

export default SettingsTab;
